import * as CookieConsent from "vanilla-cookieconsent";

/**
 * Cookie-consent gate — the single source of truth for "may we do this?".
 *
 * Two laws, two questions, and only the first one lives here:
 *  1. ePrivacy (Austria: TKG 2021 §165(3)) — may we write to / read from the
 *     visitor's DEVICE? Strictly necessary, or consent. It does not care who the
 *     visitor is or whether they're logged in. That is what this file answers.
 *  2. GDPR — may we process the data on our SERVER? Separate basis (contract for
 *     orders/downloads, legitimate interest for security, etc). Not this file's
 *     job. See docu/flows/legal/data-processing.md.
 *
 * Consequence worth remembering: a GDPR basis never buys you a cookie. Logging in
 * changes nothing about gate 1.
 *
 * CATEGORIES (deliberately only two — we don't run ads, and we don't ship
 * controls that govern nothing):
 *  - `necessary`  — always on: login, cart, the consent record itself, plus the
 *                   functional preferences (volume, filters, dismissals) that
 *                   only exist because the visitor chose them.
 *  - `analytics`  — opt-in: the `as_sid` device id, first-touch referrer/UTM,
 *                   page-view/search beacons, and OpenReplay session replay.
 *
 * Anonymous, non-device-linked counters (Trending velocity, aggregate page/search
 * counts) are NOT gated here — no device write and no identifier means neither law
 * applies (GDPR Recital 26). They must never be made to depend on this function.
 */

export const ANALYTICS_CATEGORY = "analytics";

/**
 * True only when the visitor actively accepted analytics. Fails closed — absent,
 * unparsable or not-yet-answered all mean NO, so a bug here silences tracking
 * rather than leaking it.
 */
export const hasTrackingConsent = (): boolean => {
  try {
    return CookieConsent.acceptedCategory(ANALYTICS_CATEGORY);
  } catch {
    return false;
  }
};


/** Open the preferences dialog (header / footer / burger "Cookie settings"). */
export const openConsentPreferences = (): void => {
  try {
    CookieConsent.showPreferences();
  } catch {
    /* library not ready yet */
  }
};

const listeners = new Set<() => void>();

/** Called by the banner wiring whenever the visitor's choice changes. */
export const notifyConsentChanged = (): void => {
  for (const cb of listeners) {
    try {
      cb();
    } catch {
      /* one bad listener must not break the others */
    }
  }
};

/**
 * Subscribe to consent changes, so a visitor who accepts *after* page load gets
 * tracking started without a reload, and one who withdraws is honoured
 * immediately. Returns an unsubscribe fn.
 */
export const onConsentChange = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};
